import {CommonModule} from "@angular/common";
import {
  Component,
  computed,
  effect,
  inject,
  Input,
  Signal,
  signal,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {ActivatedRoute, Router, RouterModule} from "@angular/router";
import {ProductionLineService} from "src/app/features/admin/data-access/production-line.service";
import {NavigationService} from "src/app/shared/data-access/navigation.service";
import {ProductionLine} from "src/app/shared/interfaces/productionLine";
import {SaveCancelButtonsComponent} from "../../../components/save-cancel-buttons/save-cancel-buttons.component";

type FormAction = "create" | "edit" | "";

@Component({
  selector: "app-production-line-form",
  templateUrl: "./production-line-form.component.html",
  styleUrls: ["./production-line-form.component.scss"],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatInputModule,
    RouterModule,
    SaveCancelButtonsComponent,
  ],
})
export class ProductionLineFormComponent {
  @Input() title: string;
  productionLineService = inject(ProductionLineService);
  navigationService = inject(NavigationService);
  formBuilder = inject(FormBuilder);
  router = inject(Router);
  action = signal<FormAction>("edit");
  route = inject(ActivatedRoute);

  productionLineForm: FormGroup = this.formBuilder.group({
    name: ["", Validators.required],
    shortcode: ["", Validators.required],
    description: [""],
  });

  private productionLineBeingEdited = computed(() => {
    const url = this.navigationService.currentUrl();
    const productionLineUuid = url.split("line/")[1]?.split("?")[0];
    if (productionLineUuid) {
      return (
        this.productionLineService
          .productionLines()
          .find((line) => line.id === productionLineUuid) || null
      );
    } else {
      return null;
    }
  });

  constructor() {
    this.action.set(
      this.route.snapshot.queryParams["formAction"] as FormAction
    );
    effect(() => {
      const productionLineEdited: ProductionLine | null =
        this.productionLineService.productionLineBeingEdited();
      if (!productionLineEdited) {
        this.productionLineForm.reset();
      } else {
        this.productionLineForm.patchValue({
          name: productionLineEdited.name,
          shortcode: productionLineEdited.shortcode,
          description: productionLineEdited.description,
        });
      }

      const line = this.productionLineBeingEdited();
      if (line) {
        this.productionLineForm.patchValue({
          name: line.name,
          shortcode: line.shortcode,
          description: line.description,
        });
      }
    });
  }

  ngOnInit() {
    this.getProductionLineFromUrl();
  }

  getProductionLineFromUrl(): Signal<ProductionLine | null> {
    return this.productionLineBeingEdited;
  }

  save() {
    if (this.action() === "create" && this.productionLineForm.valid) {
      this.productionLineService.add$.next(this.productionLineForm.value);
      alert("Neue Produktionslinie hinzugefügt");
    } else if (this.action() === "edit" && this.productionLineForm.valid) {
      this.productionLineService.update$.next({
        id: this.productionLineBeingEdited()?.id!,
        name: this.productionLineForm.get("name")!.value,
        shortcode: this.productionLineForm.get("shortcode")!.value,
        description: this.productionLineForm.get("description")!.value,
      });
      alert("Änderungen gespeichert");
    }
    this.router.navigate(["/admin/productionLines"]);
  }
}
